import { type ReactNode, useContext } from "react";

import { Box, Column, Row, Skeleton, SkeletonBox } from "@hightouch/htui";

import { Page } from "src/components/layout";
import { CrumbBarEndSlot } from "src/components/layout/nav/mini-nav/mini-nav-context";
import { PageRailContext } from "src/components/page-rail";
import { CanvasMiniNav } from "src/pages/projects/library/canvas/canvas-mini-nav";
import { headerPillStyle } from "src/pages/projects/library/canvas/header-pill";
import { useCanvasDotGridGround } from "src/pages/projects/library/canvas/canvas-grid";
import { useReleasedScrollbarGutter } from "src/pages/projects/use-released-scrollbar-gutter";

/** Faint stand-ins for the canvas's cards, in a document's proportions. */
const FRAMES = [0, 1, 2];

/** The canvas route's fallback while the canvas page's code loads, in the page's own shell. */
export function CanvasPageSkeleton() {
  // The same release as the canvas page, so the right edge does not jump while its code loads.
  useReleasedScrollbarGutter();
  return (
    <CanvasFrame>
      <Page contain contentFlush title="Canvas" variant="flush">
        <CanvasSkeleton />
      </Page>
    </CanvasFrame>
  );
}

/** The canvas's edge against the agent chat beside it, shared with its loading fallback so the edge does not jump in. */
export function CanvasFrame({ children }: { children: ReactNode }) {
  // Closed, the chat takes no room, and the canvas runs to the window's edge with no line along it.
  const isBesideChat = useContext(PageRailContext);
  return (
    <Column
      flex={1}
      minWidth={0}
      minHeight={0}
      // 1px, not hairline: the edge has to hold against the chat at a glance.
      borderRightWidth={isBesideChat ? "1px" : 0}
      borderColor="base.border"
    >
      {children}
    </Column>
  );
}

/** The canvas's chrome while the canvas loads, placed where the loaded canvas draws it. The area's agent chat stays mounted beside it. */
export function CanvasSkeleton() {
  const gridGround = useCanvasDotGridGround();
  return (
    <Box
      aria-busy
      display="flex"
      flex={1}
      minHeight={0}
      height="100%"
      position="relative"
      overflow="hidden"
      isolation="isolate"
    >
      <Box
        flex={1}
        minHeight="400px"
        position="relative"
        style={gridGround}
        // The toolbar's buttons take no focus or click until the real canvas replaces them.
        {...{ inert: "" }}
      >
        {/* Skeleton's wrapper has no height, so each part pins to this box. */}
        <Skeleton isLoading>
          <Row
            position="absolute"
            inset={0}
            align="center"
            justify="center"
            gap={10}
            opacity={0.5}
          >
            {FRAMES.map((frame) => (
              <Column key={frame} width={56} gap={1.5}>
                <Row height={6} align="center">
                  <SkeletonBox height={2.5} width="40%" borderRadius="full" />
                </Row>
                <SkeletonBox
                  borderRadius="2xl"
                  sx={{ aspectRatio: "1 / 1.4167" }}
                />
              </Column>
            ))}
          </Row>
          {/* Mirrors the zoom pill: two round steps around a 56px value. */}
          <Row
            position="absolute"
            bottom={4}
            right={4}
            align="center"
            {...headerPillStyle}
          >
            <SkeletonBox boxSize={8} borderRadius="full" />
            <SkeletonBox height={4} width={10} mx={2} borderRadius="full" />
            <SkeletonBox boxSize={8} borderRadius="full" />
          </Row>
        </Skeleton>
      </Box>
      {/* Mirrors `TopRightChrome`, outside the inert box, so the agent panel's toggle at its end works while the canvas loads. */}
      <Row position="absolute" top={4} right={4} align="center" gap={2}>
        <Row align="center" {...headerPillStyle}>
          <Skeleton isLoading>
            <Row align="center" gap={0.5}>
              <SkeletonBox boxSize={8} borderRadius="full" />
              <SkeletonBox boxSize={8} borderRadius="full" />
            </Row>
          </Skeleton>
        </Row>
        <Row align="center" gap={0.5} {...headerPillStyle}>
          <Skeleton isLoading>
            <Row align="center" gap={0.5}>
              <SkeletonBox boxSize={8} borderRadius="full" />
              <SkeletonBox boxSize={8} borderRadius="full" />
            </Row>
          </Skeleton>
          <CrumbBarEndSlot placement="pill" gap={0.5} mr={0} />
        </Row>
      </Row>
      {/* Mirrors the title pill, outside the inert box: the navigation works while a canvas loads, or fails to. */}
      <Row
        position="absolute"
        top={4}
        left={4}
        align="center"
        gap={0.5}
        {...headerPillStyle}
      >
        <CanvasMiniNav />
        <Skeleton isLoading>
          <Row align="center" gap={0.5}>
            <SkeletonBox boxSize={8} borderRadius="full" />
            <SkeletonBox height={4} width={28} mx={2} borderRadius="full" />
          </Row>
        </Skeleton>
      </Row>
    </Box>
  );
}
